import type { WebErrorHandler } from '../web/server/types.ts';
import type { ServerWebExchange } from '../../types/web/server';
import { HttpServerRequest, HttpServerResponse } from './exchange.ts';
import { isClientDisconnectedError } from '../web/util/client.ts';
import serverRequest from '../web/async/fn/server/request.ts';
import { route } from '../web/async/fn/server/router.ts';
import serverResponse from '../web/async/fn/server/response.ts';
import { MIMEType } from 'node:util';
import { HttpStatus } from '../http/status.ts';
import type { RouterFunction, ServerRequest, ServerResponse } from '../web/async/fn/server/types.ts';
import { predicates as r} from '../web/async/fn/server/router.ts';
import { ResponseStatusError } from '../web/server/error.ts';
import type { HttpMessageReader, HttpMessageWriter } from '../http/codec/types.ts';
import { ALL } from '../utils/mime-type-utils.ts';

export type ErrorAttributeKey = 'error' | 'trace' | 'message' | 'errors' | 'timestamp' | 'status' | 'reason' | 'requestId' | 'path';

export type ErrorAttributeOptions = {
    readonly includes: ReadonlyArray<ErrorAttributeKey>;
}

export interface ErrorAttributes {
    getErrorAttributes(request: ServerRequest, options: ErrorAttributeOptions): Partial<Record<ErrorAttributeKey, unknown>>;

    getError(request: ServerRequest): Error;

    storeErrorInformation(error: Error, exchange: ServerWebExchange): void;
}

export class DefaultErrorAttributes implements ErrorAttributes {
    static readonly ERROR_ATTRIBUTE = 'io.interop.web.server.exchange.error';

    getErrorAttributes(request: ServerRequest, options: ErrorAttributeOptions): Partial<Record<ErrorAttributeKey, unknown>> {
        const erroroAttributes = this.#getErrorAttributesInternal(request, options.includes.includes('trace'));
        return erroroAttributes;
    }

    getError(request: ServerRequest): Error {
        const error = request.attributes.get(DefaultErrorAttributes.ERROR_ATTRIBUTE);
        if (error === undefined) {
            throw new Error('No error found in request attributes');
        }
        return error as Error;
    }

    storeErrorInformation(error: Error, exchange: ServerWebExchange): void {
        exchange.attributes.set(DefaultErrorAttributes.ERROR_ATTRIBUTE, error);
    }

    #getErrorAttributesInternal(request: ServerRequest, includeStackTrace: boolean) {
        const errorAttributes: Partial<Record<ErrorAttributeKey, unknown>> = {};
        errorAttributes.timestamp = new Date();
        const error = this.getError(request);
        const errorStatus = this.#determineHttpStatus(error);
        errorAttributes.status = errorStatus.value;
        errorAttributes.reason = errorStatus.phrase;
        errorAttributes.requestId = request.exchange.request.id;

        this.#handleError(errorAttributes, error, includeStackTrace);
        return errorAttributes;

    }

    #determineHttpStatus(error: Error): HttpStatus {
        if (error instanceof ResponseStatusError) {
            const httpStatus = HttpStatus.resolve(error.statusCode.value);
            if (httpStatus) {
                return httpStatus;
            }
        }
        return HttpStatus.INTERNAL_SERVER_ERROR;
    }

    #handleError(errorAttributes: Partial<Record<ErrorAttributeKey, unknown>>, error: Error, includeStackTrace: boolean) {

        if (error instanceof ResponseStatusError) {
            errorAttributes.message = error.reason;
        }
    }
}

export abstract class AbstractWebErrorHandler implements WebErrorHandler {

    readonly #errorAttributes: ErrorAttributes;
    readonly #messageReaders: readonly HttpMessageReader[];
    readonly #messageWriters: readonly HttpMessageWriter[];

    protected constructor(messageReaders: readonly HttpMessageReader[],
                          messageWriters: readonly HttpMessageWriter[],
                          errorAttributes: ErrorAttributes) {
        if (!messageWriters) {
            throw new Error('messageWriters must not be null.');
        }
        this.#errorAttributes = errorAttributes;
        this.#messageReaders = messageReaders;
        this.#messageWriters = messageWriters;
    }

    getErrorAttributes(request: ServerRequest, options: ErrorAttributeOptions) {
        return this.#errorAttributes.getErrorAttributes(request, options);
    }

    async handle(exchange: ServerWebExchange<HttpServerRequest, HttpServerResponse>, error: Error): Promise<void> {
        if (exchange.response.commited || this.#isDisconnectedClientError(error)) {
            return Promise.reject(error);
        }
        this.#errorAttributes.storeErrorInformation(error, exchange);
        const request = serverRequest.create(exchange, this.#messageReaders);
        const handler = await this.getRoutingFunction(this.#errorAttributes)?.route(request);
        if (handler) {
            const response = await handler(request);
            this.logError(request, response, error);
            await this.#write(exchange, response);
        }
        else {
            throw error;
        }
    }

    protected abstract getRoutingFunction(errorAttributes: ErrorAttributes): RouterFunction<ServerResponse>;


    protected logError(request: ServerRequest, response: ServerResponse, error: Error) {

    }

    #isDisconnectedClientError(error: Error) {
        return isClientDisconnectedError(error);
    }

    async #write(exchange: ServerWebExchange<HttpServerRequest, HttpServerResponse>, response: ServerResponse) {
        exchange.response.headers.set('Content-Type', response.headers.get('Content-Type'));
        const responseContext = {
            messageWriters: this.#messageWriters
        };
        return response.writeTo(exchange, responseContext);
    }
}

export type ErrorProperties = {}

export class DefaultWebErrorHandler extends AbstractWebErrorHandler {
    static readonly #defaultErrorAttributes: DefaultErrorAttributes = new DefaultErrorAttributes();
    static readonly #ONLY_STATUS: ErrorAttributeOptions = { includes: ['status'] };

    constructor(readers: ReadonlyArray<HttpMessageReader>,
                writers: ReadonlyArray<HttpMessageWriter>,
                errorAttributes: ErrorAttributes,
                errorProperties: ErrorProperties = {}) {
        super(readers, writers, errorAttributes);
    }

    protected getRoutingFunction(errorAttributes: ErrorAttributes): RouterFunction<ServerResponse> {
        return route(r.all(), (request) => {
            return this.#renderErrorResponse(request);
        });
    }

    #getErrorAttributes(request: ServerRequest, mimeType: MIMEType) {
        return super.getErrorAttributes(request, this.getErrorAttributeOptions(request, mimeType));
    }

    protected getErrorAttributeOptions(request: ServerRequest, mimeType: MIMEType): ErrorAttributeOptions {
        return {
            includes: ['error', 'message', 'status', 'path']
        };
    }

    async #renderErrorResponse(request: ServerRequest): Promise<ServerResponse> {
        const errorAttributes = this.#getErrorAttributes(request, ALL);
        const status = this.#getHttpStatus(request, errorAttributes);
        return serverResponse.status(status).contentType(new MIMEType('application/json')).bodyValue(JSON.stringify(errorAttributes));
    }

    #getHttpStatus(request: ServerRequest, errorAttributes: Partial<Record<ErrorAttributeKey, unknown>>): number {
        return this.getHttpStatus(errorAttributes.status !== undefined ? errorAttributes
            : DefaultWebErrorHandler.#defaultErrorAttributes.getErrorAttributes(request, DefaultWebErrorHandler.#ONLY_STATUS));
    }

    protected getHttpStatus(errorAttributes: Partial<Record<ErrorAttributeKey, unknown>>): number {
        const status = errorAttributes.status;
        if (typeof status === 'number') {
            return status;
        }
        throw new Error(`Invalid status code: ${status}`);
    }
}
